// 051 parte (c): GESTACIÓN ACTUAL contra el papel (form. 051 mayo/2016, pág. 178 del PDF oficial).
//
// Cada casilla del papel con círculos "no / si" es tri-estado (`SiNoBotones`: Sí / No / sin responder) y el "No" llega al `Par[]`
// firmado. Las casillas con más de dos círculos (n/c, s/d, n/d, "no se hizo", "no aplica", + / −) son una elección única
// (`EleccionBotones`): volver a pulsar la opción activa la limpia. Este formulario solo ESCRIBE el `Par[]` (no relee lo guardado),
// así que reemplazar el modelo viejo no exige migrar nada.
//
// Sin hijos huérfanos: lo que cuelga de un padre (resultado del VIH si la prueba se solicitó; semanas de una prueba de sífilis si
// tiene resultado) no se imprime cuando el padre está en No / n/c / vacío, aunque haya quedado escrito.
import type { Par } from './index';
import { leerSiNo, textoSiNo, type SiNoValor } from './camposSimplesDatos';
import type { Traducir } from './tecnicaAnestesicaDatos';

const P = 'formularios.f051.gestacionActual';

/** Los tres trimestres del papel (columnas de fuma/drogas/alcohol/violencia). */
export const TRIMESTRES_051 = ['1', '2', '3'] as const;
export const FACTORES_RIESGO_051 = ['fumaActual', 'fumaPasivo', 'drogas', 'alcohol', 'violencia'] as const;
export type FactorRiesgo051 = (typeof FACTORES_RIESGO_051)[number];

/** Clave de `factores`: un Sí/No por factor y trimestre. */
export function claveFactor051(factor: string, trimestre: string): string {
  return `${factor}.${trimestre}`;
}

export const ANTIRRUBEOLA_OPCIONES = ['previa', 'noSabe', 'embarazo', 'no'] as const;
export type Antirrubeola = (typeof ANTIRRUBEOLA_OPCIONES)[number];

export const CERVIX_EXAMENES_OPCIONES = ['inspeccionVisual', 'pap', 'colposcopia'] as const;
export const CERVIX_RESULTADO_OPCIONES = ['normal', 'anormal', 'noSeHizo'] as const;
export type CervixResultado = (typeof CERVIX_RESULTADO_OPCIONES)[number];

export const RH_OPCIONES = ['neg', 'pos'] as const;
export type Rh = (typeof RH_OPCIONES)[number];

/** Toxoplasmosis, Chagas, Paludismo/Malaria y Estreptococo B comparten círculos "− / + / no se hizo [/ no aplica]". */
export const TOXOPLASMOSIS_OPCIONES = ['neg', 'pos', 'noSeHizo', 'noAplica'] as const;
export const CHAGAS_PALUDISMO_OPCIONES = ['neg', 'pos', 'noSeHizo', 'noAplica'] as const;
export const ESTREPTOCOCO_B_OPCIONES = ['neg', 'pos', 'noSeHizo'] as const;
export type ResultadoPrueba = 'neg' | 'pos' | 'noSeHizo' | 'noAplica';

/** VIH: "solicitada" y "TARV en emb." traen si / no / n/c. */
export const SI_NO_NC_OPCIONES = ['si', 'no', 'nc'] as const;
export type SiNoNc = (typeof SI_NO_NC_OPCIONES)[number];
export const VIH_RESULTADO_OPCIONES = ['pos', 'neg', 'nc', 'nd'] as const;
export type VihResultado = (typeof VIH_RESULTADO_OPCIONES)[number];

/** Sífilis: no treponémica (− + s/d); treponémica y tratamiento (− + s/d n/c); tratamiento de la pareja (no si s/d n/c). */
export const SIFILIS_NO_TREPONEMICA_OPCIONES = ['neg', 'pos', 'sd'] as const;
export const SIFILIS_TREPONEMICA_OPCIONES = ['neg', 'pos', 'sd', 'nc'] as const;
export const SIFILIS_PAREJA_OPCIONES = ['no', 'si', 'sd', 'nc'] as const;
export type SifilisResultado = 'neg' | 'pos' | 'sd' | 'nc';
export type SifilisPareja = (typeof SIFILIS_PAREJA_OPCIONES)[number];

export const BACTERIURIA_OPCIONES = ['normal', 'anormal', 'noSeHizo'] as const;
export type Bacteriuria = (typeof BACTERIURIA_OPCIONES)[number];

/** Educación prenatal: seis círculos numerados (sesiones 1 a 6), cada uno se marca o no. */
export const SESIONES_EDUCACION_PRENATAL_051 = ['1', '2', '3', '4', '5', '6'] as const;

export interface EstadoVih051 {
  readonly solicitada: SiNoNc | '';
  readonly resultado: VihResultado | '';
  readonly tarv: SiNoNc | '';
}

export interface EstadoSifilisFila051 {
  readonly noTreponemica: SifilisResultado | '';
  readonly noTreponemicaSemanas: string;
  readonly treponemica: SifilisResultado | '';
  readonly treponemicaSemanas: string;
  readonly tratamiento: SifilisResultado | '';
  readonly tratamientoSemanas: string;
  readonly tratamientoPareja: SifilisPareja | '';
}

/** Glucemia en ayunas: un círculo "≥ 92 mg/dl" (alerta, se marca o no) y el valor. */
export interface EstadoGlucemia051 {
  readonly valor: string;
  readonly mayorIgual92: boolean;
}

/** Hb: el valor y el círculo "< 11.0 g/dl" (alerta, se marca o no). */
export interface EstadoHb051 {
  readonly valor: string;
  readonly menor11: boolean;
}

export interface EstadoGestacionActual051 {
  readonly pesoInicial: string;
  readonly talla: string;
  readonly egConfiableFum: SiNoValor;
  readonly egConfiableEco: SiNoValor;
  readonly ecografia11a13Semanas: SiNoValor;
  readonly factores: Readonly<Record<string, SiNoValor>>;
  readonly antirrubeola: Antirrubeola | '';
  readonly antitetanicaVigente: SiNoValor;
  readonly antitetanicaDosis1: string;
  readonly antitetanicaDosis2: string;
  readonly examenNormalMamas: SiNoValor;
  readonly examenNormalOdontologico: SiNoValor;
  readonly cervix: Readonly<Record<string, CervixResultado | ''>>;
  readonly grupo: string;
  readonly rh: Rh | '';
  readonly inmunizacion: SiNoValor;
  readonly toxoplasmosis: ResultadoPrueba | '';
  readonly vihMenor20: EstadoVih051;
  readonly vihMayorIgual20: EstadoVih051;
  readonly hbMenor20: EstadoHb051;
  readonly feIndicado: SiNoValor;
  readonly folatosIndicados: SiNoValor;
  readonly hbMayorIgual20: EstadoHb051;
  readonly sifilisMenor20: EstadoSifilisFila051;
  readonly sifilisMayorIgual20: EstadoSifilisFila051;
  readonly chagas: ResultadoPrueba | '';
  readonly paludismoMalaria: ResultadoPrueba | '';
  readonly bacteriuriaMenor20: Bacteriuria | '';
  readonly bacteriuriaMayorIgual20: Bacteriuria | '';
  readonly glucemiaMenor20: EstadoGlucemia051;
  readonly glucemiaMayor30: EstadoGlucemia051;
  readonly calcio: SiNoValor;
  /** Ácido acetilsalicílico > 12 sem: no / si / n/c (un círculo más que el calcio). */
  readonly acidoAcetilsalicilico: SiNoNc | '';
  readonly estreptococoB: ResultadoPrueba | '';
  readonly educacionPrenatal: Readonly<Record<string, boolean>>;
  readonly planPartoEmergencia: SiNoValor;
  readonly consejeriaLactanciaMaterna: SiNoValor;
}

const vihVacio = (): EstadoVih051 => ({ solicitada: '', resultado: '', tarv: '' });
const sifilisVacia = (): EstadoSifilisFila051 => ({ noTreponemica: '', noTreponemicaSemanas: '', treponemica: '', treponemicaSemanas: '', tratamiento: '', tratamientoSemanas: '', tratamientoPareja: '' });
const glucemiaVacia = (): EstadoGlucemia051 => ({ valor: '', mayorIgual92: false });
const hbVacia = (): EstadoHb051 => ({ valor: '', menor11: false });

export function estadoInicialGestacionActual051(): EstadoGestacionActual051 {
  return {
    pesoInicial: '',
    talla: '',
    egConfiableFum: '',
    egConfiableEco: '',
    ecografia11a13Semanas: '',
    factores: {},
    antirrubeola: '',
    antitetanicaVigente: '',
    antitetanicaDosis1: '',
    antitetanicaDosis2: '',
    examenNormalMamas: '',
    examenNormalOdontologico: '',
    cervix: {},
    grupo: '',
    rh: '',
    inmunizacion: '',
    toxoplasmosis: '',
    vihMenor20: vihVacio(),
    vihMayorIgual20: vihVacio(),
    hbMenor20: hbVacia(),
    feIndicado: '',
    folatosIndicados: '',
    hbMayorIgual20: hbVacia(),
    sifilisMenor20: sifilisVacia(),
    sifilisMayorIgual20: sifilisVacia(),
    chagas: '',
    paludismoMalaria: '',
    bacteriuriaMenor20: '',
    bacteriuriaMayorIgual20: '',
    glucemiaMenor20: glucemiaVacia(),
    glucemiaMayor30: glucemiaVacia(),
    calcio: '',
    acidoAcetilsalicilico: '',
    estreptococoB: '',
    educacionPrenatal: {},
    planPartoEmergencia: '',
    consejeriaLactanciaMaterna: '',
  };
}

/** El resultado del VIH solo existe si la prueba se solicitó. */
export function vihTieneResultado051(v: EstadoVih051): boolean {
  return v.solicitada === 'si';
}

/** Las semanas de una prueba/tratamiento de sífilis solo tienen sentido con un resultado − o +. */
export function sifilisTieneSemanas051(resultado: SifilisResultado | ''): boolean {
  return resultado === 'neg' || resultado === 'pos';
}

function siNo(t: Traducir, v: SiNoValor): string {
  return textoSiNo(v, { si: t('formulario.si'), no: t('formulario.no') });
}

function opcion(t: Traducir, valor: string): string {
  return valor ? t(`${P}.opcion.${valor}`) : '';
}

function par(t: Traducir, clave: string, respuesta: string, sufijo = ''): Par {
  return { pregunta: sufijo ? `${t(`${P}.${clave}`)} — ${sufijo}` : t(`${P}.${clave}`), respuesta };
}

function paresVih(t: Traducir, grupo: 'menor20' | 'mayorIgual20', v: EstadoVih051): Par[] {
  const g = t(`${P}.grupos.${grupo}`);
  const titulo = t(`${P}.vih.titulo`);
  const pares: Par[] = [{ pregunta: `${titulo} ${g} — ${t(`${P}.vih.solicitada`)}`, respuesta: opcion(t, v.solicitada) }];
  if (vihTieneResultado051(v)) pares.push({ pregunta: `${titulo} ${g} — ${t(`${P}.vih.resultado`)}`, respuesta: opcion(t, v.resultado) });
  pares.push({ pregunta: `${titulo} ${g} — ${t(`${P}.vih.tarv`)}`, respuesta: opcion(t, v.tarv) });
  return pares;
}

function paresSifilis(t: Traducir, grupo: 'menor20' | 'mayorIgual20', f: EstadoSifilisFila051): Par[] {
  const pre = `${t(`${P}.sifilis.titulo`)} ${t(`${P}.grupos.${grupo}`)}`;
  const semanas = (resultado: SifilisResultado | '', valor: string): string => (sifilisTieneSemanas051(resultado) ? valor.trim() : '');
  const s = t(`${P}.sifilis.semanas`);
  return [
    { pregunta: `${pre} — ${t(`${P}.sifilis.noTreponemica`)}`, respuesta: opcion(t, f.noTreponemica) },
    { pregunta: `${pre} — ${t(`${P}.sifilis.noTreponemica`)} (${s})`, respuesta: semanas(f.noTreponemica, f.noTreponemicaSemanas) },
    { pregunta: `${pre} — ${t(`${P}.sifilis.treponemica`)}`, respuesta: opcion(t, f.treponemica) },
    { pregunta: `${pre} — ${t(`${P}.sifilis.treponemica`)} (${s})`, respuesta: semanas(f.treponemica, f.treponemicaSemanas) },
    { pregunta: `${pre} — ${t(`${P}.sifilis.tratamiento`)}`, respuesta: opcion(t, f.tratamiento) },
    { pregunta: `${pre} — ${t(`${P}.sifilis.tratamiento`)} (${s})`, respuesta: semanas(f.tratamiento, f.tratamientoSemanas) },
    { pregunta: `${pre} — ${t(`${P}.sifilis.tratamientoPareja`)}`, respuesta: opcion(t, f.tratamientoPareja) },
  ];
}

function paresGlucemia(t: Traducir, grupo: 'menor20' | 'mayor30', g: EstadoGlucemia051): Par[] {
  const pre = `${t(`${P}.glucemia.titulo`)} ${t(`${P}.glucemia.${grupo}`)}`;
  return [
    { pregunta: pre, respuesta: g.valor.trim() },
    { pregunta: `${pre} — ${t(`${P}.glucemia.mayorIgual92`)}`, respuesta: g.mayorIgual92 ? t('formulario.si') : '' },
  ];
}

function paresHb(t: Traducir, clave: 'hbMenor20' | 'hbMayorIgual20', h: EstadoHb051): Par[] {
  return [
    { pregunta: t(`${P}.${clave}`), respuesta: h.valor.trim() },
    { pregunta: `${t(`${P}.${clave}`)} — ${t(`${P}.hbMenor11`)}`, respuesta: h.menor11 ? t('formulario.si') : '' },
  ];
}

/** `Par[]` de toda la sección, en el orden del papel (izquierda a derecha, fila a fila). Los vacíos los filtra quien la llama. */
export function paresDeGestacionActual051(t: Traducir, e: EstadoGestacionActual051): Par[] {
  const pares: Par[] = [
    par(t, 'pesoInicial', e.pesoInicial.trim()),
    par(t, 'talla', e.talla.trim()),
    par(t, 'egConfiableFum', siNo(t, e.egConfiableFum)),
    par(t, 'egConfiableEco', siNo(t, e.egConfiableEco)),
    par(t, 'ecografia11a13Semanas', siNo(t, e.ecografia11a13Semanas)),
  ];
  for (const f of FACTORES_RIESGO_051) {
    for (const tri of TRIMESTRES_051) pares.push({ pregunta: `${t(`${P}.factores.${f}`)} — ${t(`${P}.trimestres.${tri}`)}`, respuesta: siNo(t, leerSiNo(e.factores[claveFactor051(f, tri)])) });
  }
  pares.push(
    par(t, 'antirrubeola', opcion(t, e.antirrubeola)),
    par(t, 'antitetanicaVigente', siNo(t, e.antitetanicaVigente)),
    par(t, 'antitetanicaDosis1', e.antitetanicaDosis1.trim()),
    par(t, 'antitetanicaDosis2', e.antitetanicaDosis2.trim()),
    par(t, 'examenNormalMamas', siNo(t, e.examenNormalMamas)),
    par(t, 'examenNormalOdontologico', siNo(t, e.examenNormalOdontologico))
  );
  for (const c of CERVIX_EXAMENES_OPCIONES) pares.push({ pregunta: `${t(`${P}.cervix.titulo`)} — ${t(`${P}.cervix.${c}`)}`, respuesta: opcion(t, e.cervix[c] ?? '') });
  pares.push(
    par(t, 'grupo', e.grupo.trim()),
    par(t, 'rh', opcion(t, e.rh)),
    par(t, 'inmunizacion', siNo(t, e.inmunizacion)),
    par(t, 'toxoplasmosis', opcion(t, e.toxoplasmosis)),
    ...paresVih(t, 'menor20', e.vihMenor20),
    ...paresVih(t, 'mayorIgual20', e.vihMayorIgual20),
    ...paresHb(t, 'hbMenor20', e.hbMenor20),
    par(t, 'feIndicado', siNo(t, e.feIndicado)),
    par(t, 'folatosIndicados', siNo(t, e.folatosIndicados)),
    ...paresHb(t, 'hbMayorIgual20', e.hbMayorIgual20),
    ...paresSifilis(t, 'menor20', e.sifilisMenor20),
    ...paresSifilis(t, 'mayorIgual20', e.sifilisMayorIgual20),
    par(t, 'chagas', opcion(t, e.chagas)),
    par(t, 'paludismoMalaria', opcion(t, e.paludismoMalaria)),
    { pregunta: `${t(`${P}.bacteriuria`)} ${t(`${P}.grupos.menor20`)}`, respuesta: opcion(t, e.bacteriuriaMenor20) },
    { pregunta: `${t(`${P}.bacteriuria`)} ${t(`${P}.grupos.mayorIgual20`)}`, respuesta: opcion(t, e.bacteriuriaMayorIgual20) },
    ...paresGlucemia(t, 'menor20', e.glucemiaMenor20),
    ...paresGlucemia(t, 'mayor30', e.glucemiaMayor30),
    par(t, 'calcio', siNo(t, e.calcio)),
    par(t, 'acidoAcetilsalicilico', opcion(t, e.acidoAcetilsalicilico)),
    par(t, 'estreptococoB', opcion(t, e.estreptococoB)),
    par(t, 'educacionPrenatal', SESIONES_EDUCACION_PRENATAL_051.filter((s) => e.educacionPrenatal[s]).join(', ')),
    par(t, 'planPartoEmergencia', siNo(t, e.planPartoEmergencia)),
    par(t, 'consejeriaLactanciaMaterna', siNo(t, e.consejeriaLactanciaMaterna))
  );
  return pares;
}
